import React from 'react';
import { FormattedMessage } from 'react-intl';
import Masonry from './masonry';
import {FetchKeyStateContext} from '../FetchKey';
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay/hooks';
import {ErrorBoundary} from 'react-error-boundary';
import SuspenseList from '../SuspenseList';
import useQuery from '../../useQuery';
import Button, {RouterLinkButton} from '../button/button';
import Skeleton from '../Skeleton';
import EventCard from '../event-card/event-card';

const number = process.env.NODE_ENV === 'development' ? 2 : null;
const breakpointCols = {default: 3, 767: 1};

const ServiceListImpl = React.memo(({viewer, redirectOnAction, startEventIndex}) => {
    const {data, loadNext, hasNext, isLoadingNext} = usePaginationFragment(
        graphql`
            fragment serviceListComponentsImpl_viewer on Viewer @refetchable(queryName: "ServiceListComponentsImplQuery") {
                targetServices(first: $first, after: $after) @connection(key: "ServiceListComponents_targetServices") {
                    edges {
                        node {
                            id
                            slug
                            name
                            nameUk
                            details
                            detailsUk
                            location
                            locationUk
                            start
                            end
                            strategy
                            every
                            warp
                            target
                            description
                            pin
                        }
                    }
                }
            }
            
        `,
        viewer
    );
    
    return (
        <div>
            <SuspenseList revealOrder='forwards'>
                <Masonry
                    breakpointCols={breakpointCols}
                    className='masonry-grid'
                    columnClassName='masonry-grid_column'
                >
                    {data.targetServices.edges.filter((e, index) => index >= startEventIndex).map(e => e.node).map((service) =>
                        <div
                            key={service.id}
                            data-insights-object-id={service.id}
                            data-service-slug={service.slug}
                        >
                            <ErrorBoundary>
                                <React.Suspense fallback={
                                    <div className='background-color-white border-radius-0dot75rem padding-left-1dot5rem padding-top-1dot5rem padding-right-1dot5rem padding-bottom-1dot5rem shadow-large-light'>
                                        <Skeleton/>
                                    </div>
                                }>
                                    <EventCard {...{service, highlightCover: true}} />
                                </React.Suspense>
                            </ErrorBoundary>
                        </div>
                    )}
                </Masonry>
                <div/>
            </SuspenseList>
            {hasNext && !redirectOnAction &&
                <div className='margin-top-1dot5rem display-flex justify-content-center'>
                    <Button {...{size: 'xl', color:'secondary-gray', disabled: isLoadingNext, clickHandler: () => !isLoadingNext && loadNext(number || 8)}}>
                        <FormattedMessage defaultMessage='More events' />
                        {isLoadingNext && '...'}
                    </Button>
                </div>
            }
            {hasNext && redirectOnAction &&
                <div className='margin-top-1dot5rem display-flex justify-content-center'>
                    <RouterLinkButton {...{size: 'xl', color: 'secondary-gray', to: redirectOnAction}}>
                        <FormattedMessage defaultMessage='More events' />
                    </RouterLinkButton>
                </div>
            }
        </div>
    );
});

const ServiceList = ({redirectOnAction, startEventIndex = 0}) => {
    const fetchKeyState = React.useContext(FetchKeyStateContext);
    const {fromMore} = useQuery();
    const data = useLazyLoadQuery(
        graphql`
            query serviceListComponentsQuery($first: Int!, $after: String) {
                viewer {
                    ...serviceListComponentsImpl_viewer
                }
            }
        `,
        {first: fromMore ? ((number || 8) * 2 + startEventIndex) : ((number || 8) + startEventIndex)},
        {fetchKey: fetchKeyState}
    );
    return (
        <ServiceListImpl {...{viewer: data.viewer, redirectOnAction, startEventIndex}}/>
    );
};

export default React.memo(ServiceList);